import React, { SetStateAction, useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import {
	FrontendOrderItem,
	FrontendProduct,
	FrontendService,
	InstallFamiliarizeSelection,
	IQSelection,
	RepairSelection,
} from "node-library/dist/portal";
import { createUUID } from "node-library/dist/helpers/request_helper";
import {
	InstrumentType,
	InstrumentCondition,
	Severity,
	InstrumentSource,
	OrderItemType,
	InstallFamiliarizeOptions,
	WarrantyUnit,
	RepairOptions,
} from "node-library/dist/constants";
import { PrimaryColor } from "node-library/dist/colors";
import { useSnackbar } from "../../../common-components/toast/showToastMessage";
import { GreenTickIcon } from "../../../common-components/toast/successIcons";
import {
	calculatePrice,
	formatPrice,
	getInstallFamiliarizeTranslationText,
	getProductConditionTranslationText,
	getProductSourceTranslationText,
	getProductTypeTranslationText,
	getRepairOptionTranslationText,
} from "../../../helpers/sampleHelper";
import { PartSerialPair } from "node-library/dist/database";
import { Card, Divider, InputAdornment } from "@mui/material";
import {
	CommonSwitch,
	CustomCheckbox,
	CustomDropdown,
	CustomNumberInput,
	CustomPriceInput,
	CustomTextInput,
	StandardRadioGroup,
} from "../../../common-components/inputs";
import {
	AddIcon,
	DeleteIcon,
	InstrumentInfoIcon,
	WrenchIcon,
} from "../../../common-components/svgIcons";
import { BlueButton, TextButton } from "../../../common-components/button";
import { YellowWarnIcon } from "../../../common-components/toast/warnIcons";

interface Props {
	itemToUpdate: FrontendOrderItem | null;

	isInAdminView?: boolean;
	isServiceOnly: boolean;
	currency: string;
	addItem: (newItem: FrontendOrderItem) => void;
	updateItem: (itemToUpdate: FrontendOrderItem) => void;
	goBackToOrderPage: () => void;
	discardItem: () => void;
	setUnsavedChanges: React.Dispatch<SetStateAction<boolean>>;
}

export default function ItemInfoContent(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const { setUnsavedChanges, isServiceOnly, currency } = props;

	const itemToUpdate = props.itemToUpdate;

	//step 2: item info
	const [productName, setProductName] = useState<string>(
		itemToUpdate?.product.productName || ""
	);
	const [productDescription, setProductDescription] = useState<string>(
		itemToUpdate?.product.productDescription || ""
	);
	const [type, setType] = useState<InstrumentType>(
		itemToUpdate?.product.type || InstrumentType.MASS_SPECTROMETRY
	);
	const [source, setSource] = useState<InstrumentSource>(
		itemToUpdate?.product.source || InstrumentSource.SPECTRALAB_SCIENTIFIC
	);
	const [productCondition, setProductCondition] = useState<InstrumentCondition>(
		itemToUpdate?.product.productCondition || InstrumentCondition.LIKE_NEW
	);

	const [instrumentUnitPrice, setInstrumentUnitPrice] = useState<number>(
		itemToUpdate?.product.unitPrice || 0
	);
	const [instrumentQuantity, setInstrumentQuantity] = useState<number>(
		itemToUpdate?.product.quantity || 0
	);

	const [partSerialPairList, setPartSerialPairList] = useState<
		Array<PartSerialPair>
	>(
		itemToUpdate?.product.partSerialPairList || [
			{
				partNumber: "",
				serialNumber: "",
			},
		]
	);

	const [partSerialPairListErrorMsg, setPartSerialPairListErrorMsg] = useState<
		Array<PartSerialPair>
	>(
		itemToUpdate?.product.partSerialPairList?.length
			? itemToUpdate.product.partSerialPairList.map(() => ({
					partNumber: "",
					serialNumber: "",
			  }))
			: [
					{
						partNumber: "",
						serialNumber: "",
					},
			  ]
	);

	const [hasWarranty, setHasWarranty] = useState<boolean>(
		itemToUpdate?.product.warranty.enabled || false
	);

	const [warrantyPeriod, setWarrantyPeriod] = useState<number>(
		itemToUpdate?.product.warranty.period || 0
	);

	const [warrantyUnit, setWarrantyUnit] = useState<WarrantyUnit>(
		itemToUpdate?.product.warranty.unit || WarrantyUnit.DAY
	);

	// const [warrantyDueOn, setWarrantyDueOn] = useState<Date | undefined>(
	// 	itemToUpdate?.product.warrantyDueOn
	// 		? new Date(itemToUpdate?.product.warrantyDueOn)
	// 		: undefined
	// );

	// const [warranty, setWarranty] = useState<FrontendWarrantyInfo>(
	// 	itemToUpdate?.product.warranty || { enabled: false }
	// );

	// const [warrantyDescription, setWarrantyDescription] = useState<string>(
	// 	itemToUpdate?.product.warrantyDescription || ""
	// );

	const [expectedCompletionTime, setExpectedCompletionTime] = useState<
		Date | undefined
	>(
		itemToUpdate?.product.expectedCompletionTime
			? new Date(itemToUpdate?.product.expectedCompletionTime)
			: undefined
	);

	const [orderItemType, setOrderItemType] = useState<OrderItemType>(
		itemToUpdate?.itemType || OrderItemType.INSTRUMENT
	);

	//step 3: Service Info
	const [serviceUnitPrice, setServiceUnitPrice] = useState<number>(
		itemToUpdate?.service?.unitPrice || 0
	);

	const [serviceQuantity, setServiceQuantity] = useState<number>(
		itemToUpdate?.service?.quantity || 1
	);

	const [installFamiliarize, setInstallFamiliarize] =
		useState<InstallFamiliarizeSelection>(
			itemToUpdate?.service?.installFamiliarize.enabled
				? itemToUpdate?.service?.installFamiliarize
				: { enabled: false, option: InstallFamiliarizeOptions.ON_SITE }
		);

	const [instrumentQualification, setInstrumentQualification] =
		useState<IQSelection>(
			itemToUpdate?.service?.instrumentQualification || {
				enabled: false,
				preventativeMaintenance: false,
				installationQualification: false,
				operationalQualification: false,
				performanceQualification: false,
			}
		);

	const [repair, setRepair] = useState<RepairSelection>(
		itemToUpdate?.service?.repair.enabled
			? itemToUpdate?.service?.repair
			: { enabled: false, option: RepairOptions.REPAIRS }
	);

	const [hasServiceContracts, setHasServiceContracts] = useState<boolean>(
		itemToUpdate?.service?.serviceContracts.enabled || false
	);

	const [serviceContractsTime, setServiceContractsTime] = useState<number>(
		itemToUpdate?.service?.serviceContracts.enabled
			? itemToUpdate?.service?.serviceContracts.time || 0
			: 0
	);

	const [serviceDescription, setServiceDescription] = useState<string>(
		itemToUpdate?.service?.description || ""
	);

	const [serviceOtherDescription, setServiceOtherDescription] =
		useState<string>(itemToUpdate?.service?.otherDescription || "");

	const [hasServiceOtherDescription, setHasServiceOtherDescription] =
		useState<boolean>(serviceOtherDescription.length > 0);

	const [isClaimWarranty, setIsClaimWarranty] = useState<boolean>(
		itemToUpdate?.service?.claimWarranty || false
	);
	const [instrumentUnitPriceErrorMsg, setInstrumentUnitPriceErrorMsg] =
		useState<string>("");
	const [instrumentQuantityErrorMsg, setInstrumentQuantityErrorMsg] =
		useState<string>("");
	const [serviceQuantityErrorMsg, setServiceQuantityErrorMsg] =
		useState<string>("");

	const [serviceUnitPriceErrorMsg, setServiceUnitPriceErrorMsg] =
		useState<string>("");

	const [warrantyPeriodErrorMsg, setWarrantyPeriodErrorMsg] =
		useState<string>("");
	const [productNameErrorMsg, setProductNameErrorMsg] = useState<string>("");
	const [IQHasError, setIQHasError] = useState<boolean>(false);
	const [serviceContractsErrorMsg, setServiceContractsErrorMsg] =
		useState<string>("");

	const disabledService = serviceQuantity === 0;

	const required: Array<{
		condition: boolean;
		setErrorMsg: React.Dispatch<SetStateAction<string>>;
		errorMsg?: string;
	}> = [
		{
			condition: !isServiceOnly && instrumentQuantity < 1,
			setErrorMsg: setInstrumentQuantityErrorMsg,
			errorMsg: translation("PleaseEnterQuantity"),
		},
		{
			condition: isServiceOnly && serviceQuantity < 1,
			setErrorMsg: setServiceQuantityErrorMsg,
			errorMsg: translation("PleaseEnterQuantity"),
		},
		{
			condition: productName.length < 1,
			setErrorMsg: setProductNameErrorMsg,
		},
		{
			condition: !isServiceOnly && hasWarranty && warrantyPeriod < 1,
			setErrorMsg: setWarrantyPeriodErrorMsg,
		},
		{
			condition:
				isServiceOnly &&
				serviceQuantity > 0 &&
				hasServiceContracts &&
				serviceContractsTime < 1,
			setErrorMsg: setServiceContractsErrorMsg,
		},
	];

	function addOrUpdateItem() {
		let hasError = false;
		setIQHasError(false);

		required.map((field) => {
			field.setErrorMsg("");
			if (field.condition) {
				field.setErrorMsg(field.errorMsg || translation("MissingInformation"));
				hasError = true;
			}
		});
		if (
			serviceQuantity > 0 &&
			instrumentQualification.enabled &&
			!instrumentQualification.preventativeMaintenance &&
			!instrumentQualification.installationQualification &&
			!instrumentQualification.operationalQualification &&
			!instrumentQualification.performanceQualification
		) {
			setIQHasError(true);
			hasError = true;
		}

		if (hasError) {
			openSnackbar(
				translation("MissingInformation"),
				Severity.WARN,
				translation("MissingInformationError"),
				<YellowWarnIcon />
			);
			return;
		}

		let service: FrontendService | undefined;
		if (serviceQuantity > 0) {
			const serviceId = itemToUpdate?.service?.serviceId ?? createUUID();

			service = {
				serviceId,
				unitPrice: serviceUnitPrice,
				quantity: serviceQuantity,
				installFamiliarize: installFamiliarize.enabled
					? installFamiliarize
					: { enabled: false },
				instrumentQualification: instrumentQualification.enabled
					? instrumentQualification
					: {
							enabled: false,
							preventativeMaintenance: false,
							installationQualification: false,
							operationalQualification: false,
							performanceQualification: false,
					  },
				repair: repair.enabled ? repair : { enabled: false },
				serviceContracts: hasServiceContracts
					? { enabled: hasServiceContracts, time: serviceContractsTime }
					: { enabled: false },
				description: serviceDescription,
				otherDescription: hasServiceOtherDescription
					? serviceOtherDescription
					: "",
				claimWarranty: isClaimWarranty,
			};
		}

		if (itemToUpdate) {
			const product: FrontendProduct = {
				productId: itemToUpdate.product.productId,
				productName,
				productDescription,
				type,
				source,
				productCondition,
				unitPrice: instrumentUnitPrice,
				quantity: instrumentQuantity,
				partSerialPairList,
				warranty: hasWarranty
					? { enabled: true, period: warrantyPeriod, unit: warrantyUnit }
					: { enabled: false },
				expectedCompletionTime,
			};

			const orderItem: FrontendOrderItem = {
				orderItemId: itemToUpdate.orderItemId,
				product,
				service,
				itemType: orderItemType,
			};
			props.updateItem(orderItem);
			openSnackbar(
				translation("ItemUpdated"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
		} else {
			const product: FrontendProduct = {
				productId: createUUID(),
				productName,
				productDescription,
				type,
				source,
				productCondition: productCondition,
				unitPrice: instrumentUnitPrice,
				quantity: instrumentQuantity,
				partSerialPairList,
				warranty: hasWarranty
					? { enabled: true, period: warrantyPeriod, unit: warrantyUnit }
					: { enabled: false },
				expectedCompletionTime,
			};
			const orderItem: FrontendOrderItem = {
				orderItemId: createUUID(),
				product,
				service,
				itemType: orderItemType,
			};
			props.addItem(orderItem);
			openSnackbar(
				translation("ItemAddedToOrder"),
				Severity.SUCCESS,
				undefined,
				<GreenTickIcon />
			);
		}
		props.goBackToOrderPage();
	}
	function newPartSerialPair() {
		// if (testReportEmails?.length >= 5) {
		// 	openSnackbar(
		// 		translation("LimitReached"),
		// 		Severity.WARN,
		// 		translation("EmailLimitReachedDescription"),
		// 		<YellowWarnIcon />
		// 	);
		// 	return;
		// }
		setPartSerialPairList((prevItems) => [
			...prevItems,
			{ partNumber: "", serialNumber: "" },
		]);
		setPartSerialPairListErrorMsg((prevItems) => [
			...prevItems,
			{ partNumber: "", serialNumber: "" },
		]);
		setUnsavedChanges(true);
	}
	function removePartSerialPair(index: number) {
		if (index >= 0 && index < partSerialPairList.length) {
			setPartSerialPairList((prevItems) =>
				prevItems.filter((_, i) => i !== index)
			);
			setPartSerialPairListErrorMsg((prevItems) =>
				prevItems.filter((_, i) => i !== index)
			);
			setUnsavedChanges(true);
		} else {
			return;
		}
	}
	function updatePartSerialPair(index: number, newItem: PartSerialPair) {
		// Create a new array with the updated item at the specific index
		const updatedPartSerialPairList = partSerialPairList.map(
			(pair, i) => (i === index ? newItem : pair) // If the index matches, replace the item
		);

		// Update the state with the new array
		setPartSerialPairList(updatedPartSerialPairList);
		setUnsavedChanges(true);
	}

	useEffect(() => {
		setOrderItemType(
			isServiceOnly ? OrderItemType.SERVICE : OrderItemType.INSTRUMENT
		);
	}, [isServiceOnly]);

	const partSerialPairListItems: Array<React.ReactNode> =
		partSerialPairList.map((pair, index) => {
			return (
				<div
					key={index}
					style={{ display: "flex", gap: "10px", alignItems: "center" }}
				>
					<div style={{ width: "100%", display: "flex", gap: "13px" }}>
						<CustomTextInput
							id={`part-number-input-${index}`}
							value={partSerialPairList[index].partNumber}
							setValue={(value) => {
								setUnsavedChanges(true);
								updatePartSerialPair(index, {
									partNumber: value,
									serialNumber: partSerialPairList[index].serialNumber,
								});
							}}
							placeholder={`(${translation("IfApplicable")})`}
							required
							errorMsg={partSerialPairListErrorMsg[index].partNumber}
							setErrorMsg={(msg: string) => {
								const updatedMsgs = partSerialPairListErrorMsg.map(
									(pair, i) =>
										i === index ? { ...pair, partNumber: msg } : pair // If the index matches, replace the item
								);

								// Update the state with the new array
								setPartSerialPairListErrorMsg(updatedMsgs);
							}}
							className="even-space-flex-item"
						/>
						<CustomTextInput
							id={`serial-number-input-${index}`}
							value={partSerialPairList[index].serialNumber}
							setValue={(value) => {
								setUnsavedChanges(true);
								updatePartSerialPair(index, {
									partNumber: partSerialPairList[index].partNumber,
									serialNumber: value,
								});
							}}
							placeholder={`(${translation("IfApplicable")})`}
							required
							errorMsg={partSerialPairListErrorMsg[index].serialNumber}
							setErrorMsg={(msg: string) => {
								const updatedMsgs = partSerialPairListErrorMsg.map(
									(pair, i) =>
										i === index ? { ...pair, serialNumber: msg } : pair // If the index matches, replace the item
								);

								// Update the state with the new array
								setPartSerialPairListErrorMsg(updatedMsgs);
							}}
							className="even-space-flex-item"
						/>
					</div>
					<div
						id={`delete-part-serial-input-${index}`}
						style={{ cursor: "pointer" }}
						onClick={() => {
							setUnsavedChanges(true);
							removePartSerialPair(index);
						}}
					>
						<DeleteIcon></DeleteIcon>
					</div>
				</div>
			);
		});

	const partSerialNumberInput: React.ReactNode = (
		<>
			<div className="part-serial-text-field-container">
				{partSerialPairList.length > 0 && (
					<div style={{ display: "flex", marginRight: "30px", gap: "13px" }}>
						<span style={{ flex: "1" }} className="common-text-input-label">
							{translation("PartNumber")}
						</span>
						<span style={{ flex: "1" }} className="common-text-input-label">
							{translation("SerialNumber")}
						</span>
					</div>
				)}
				{partSerialPairListItems}
			</div>
			<div
				className={`typography-body-02`}
				style={{ display: "flex", flexWrap: "wrap", alignItems: "center" }}
			>
				<div className="submission-confirm-info-row-info">
					{
						<TextButton
							id={"add-part-serial-pair-button"}
							onClick={() => {
								newPartSerialPair();
							}}
							size="xsmall"
						>
							<AddIcon fillColor={PrimaryColor.PRIMARY_800} />
							<span className="typography-body-02">
								{translation("MorePartSerialNumber")}
							</span>
						</TextButton>
					}
				</div>
			</div>
		</>
	);

	const instrumentPriceCard: React.ReactNode = (
		<Card className="card-equal-padding item-price-card">
			<div className="item-price-card-input-row">
				<CustomPriceInput
					id="item-instrument-price-input"
					label={translation("UnitPrice")}
					value={instrumentUnitPrice}
					setValue={(value) => {
						setInstrumentUnitPrice(value);
						setUnsavedChanges(true);
					}}
					placeholder={"0.00"}
					currency={currency}
					required
					errorMsg={instrumentUnitPriceErrorMsg}
					setErrorMsg={setInstrumentUnitPriceErrorMsg}
				/>
				<CustomNumberInput
					id="item-instrument-quantity-input"
					label={translation("Quantity")}
					value={instrumentQuantity}
					setValue={(value) => {
						setInstrumentQuantity(value);
						setUnsavedChanges(true);
					}}
					placeholder={"0"}
					required
					errorMsg={instrumentQuantityErrorMsg}
					setErrorMsg={setInstrumentQuantityErrorMsg}
				/>
			</div>
			<div className="typography-heading-04 typography-bold item-price-card-total">
				<span>{`${translation("Total")}:`}</span>
				<span>{`${formatPrice(
					calculatePrice(instrumentUnitPrice, instrumentQuantity),
					currency
				)}`}</span>
			</div>
		</Card>
	);

	const servicePriceCard: React.ReactNode = (
		<Card className="card-equal-padding item-price-card">
			<div className="item-price-card-input-row">
				<CustomPriceInput
					id="item-service-price-input"
					label={translation("UnitPrice")}
					value={serviceUnitPrice}
					setValue={(value) => {
						setServiceUnitPrice(value);
						setUnsavedChanges(true);
					}}
					placeholder={"0.00"}
					currency={currency}
					required
					errorMsg={serviceUnitPriceErrorMsg}
					setErrorMsg={setServiceUnitPriceErrorMsg}
				/>
				<CustomNumberInput
					id="item-service-quantity-input"
					label={translation("Quantity")}
					value={serviceQuantity}
					setValue={(value) => {
						setServiceQuantity(value);
						setUnsavedChanges(true);
					}}
					placeholder={"0.00"}
					minValue={0}
					required
					errorMsg={serviceQuantityErrorMsg}
					setErrorMsg={setServiceQuantityErrorMsg}
				/>
			</div>
			<div className="typography-heading-04 typography-bold item-price-card-total">
				<span>{`${translation("Total")}:`}</span>
				<span>{`${formatPrice(
					calculatePrice(serviceUnitPrice, serviceQuantity),
					currency
				)}`}</span>
			</div>
		</Card>
	);

	const conditionSelector: React.ReactNode = (
		<CustomDropdown
			id="condition-dropdown"
			label={translation("Condition")}
			options={Object.values(InstrumentCondition).map((value) => {
				return {
					value: value,
					title: getProductConditionTranslationText(value, translation),
				};
			})}
			value={productCondition}
			setValue={setProductCondition}
			className="even-space-flex-item"
		></CustomDropdown>
	);

	const typeSelector: React.ReactNode = (
		<CustomDropdown
			id="type-dropdown"
			label={translation("InstrumentType")}
			options={Object.values(InstrumentType).map((value) => {
				return {
					value: value,
					title: getProductTypeTranslationText(value, translation),
				};
			})}
			value={type}
			setValue={setType}
			className="even-space-flex-item"
		></CustomDropdown>
	);

	const sourceSelector: React.ReactNode = (
		<CustomDropdown
			id="source-dropdown"
			label={translation("Source")}
			options={Object.values(InstrumentSource).map((value) => {
				return {
					value: value,
					title: getProductSourceTranslationText(value, translation),
				};
			})}
			value={source}
			setValue={setSource}
			className="even-space-flex-item"
		></CustomDropdown>
	);

	const warrantyInfo: React.ReactNode = isServiceOnly ? (
		<CustomCheckbox
			label={translation("ClaimWarranty")}
			labelClassName={"typography-body-01"}
			checked={isClaimWarranty}
			setChecked={setIsClaimWarranty}
		/>
	) : (
		<div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
			<div className="center-align-with-gap">
				<CommonSwitch
					id="warranty-included-switch"
					checked={hasWarranty}
					setChecked={(checked) => setHasWarranty(checked)}
				/>
				<span className="typography-body-01">
					{translation("IncludeWarranty")}
				</span>
			</div>
			{hasWarranty && (
				<div style={{ display: "flex", gap: "16px" }}>
					<CustomNumberInput
						id="item-warranty-time-input"
						label={`${translation("Warranty")}`}
						value={warrantyPeriod}
						setValue={(value) => {
							setWarrantyPeriod(value);
							setUnsavedChanges(true);
						}}
						required
						errorMsg={warrantyPeriodErrorMsg}
						setErrorMsg={setWarrantyPeriodErrorMsg}
						className="even-space-flex-item"
					/>
					<StandardRadioGroup
						options={Object.values(WarrantyUnit).map((value) => {
							return {
								value: value,
								key: value,
								name: translation(value),
							};
						})}
						selectedOption={warrantyUnit}
						onChange={(e) => {
							setWarrantyUnit(e.target.value);
						}}
						className="item-warranty-unit-radio-group"
					/>
				</div>
			)}
		</div>
	);

	const serviceOptionForm: React.ReactNode = (
		<div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
			<div className="service-option-form-item">
				<CustomCheckbox
					label={translation("InstallationAndFarmilirization")}
					labelClassName={"typography-body-01"}
					checked={installFamiliarize.enabled}
					setChecked={() => {
						setInstallFamiliarize((prev) => ({
							...prev,
							enabled: !installFamiliarize.enabled,
						}));
					}}
					className="even-space-flex-item"
					disabled={disabledService}
				/>
				<div
					style={{
						visibility: installFamiliarize.enabled ? "visible" : "hidden",

						// width: "200px", // fixed width to avoid shrinking when hidden
					}}
					className="even-space-flex-item"
				>
					<CustomDropdown
						id={`installation-farmilirization-dropdown`}
						options={Object.values(InstallFamiliarizeOptions).map((value) => {
							return {
								value: value,
								title: getInstallFamiliarizeTranslationText(value, translation),
							};
						})}
						value={installFamiliarize.option}
						setValue={(value) =>
							setInstallFamiliarize((prev) => ({
								...prev,
								option: value,
							}))
						}
						className="even-space-flex-item"
						smallSize
						disabled={disabledService}
					></CustomDropdown>
				</div>
			</div>
			<div className="service-option-form-item">
				<CustomCheckbox
					label={translation("Qualifications")}
					labelClassName={"typography-body-01"}
					checked={instrumentQualification.enabled}
					setChecked={() => {
						setInstrumentQualification((prev) => ({
							...prev,
							enabled: !instrumentQualification.enabled,
						}));
					}}
					className="even-space-flex-item"
					disabled={disabledService}
				/>
				<div
					style={{
						visibility: instrumentQualification.enabled ? "visible" : "hidden",

						// width: "200px", // fixed width to avoid shrinking when hidden
					}}
					className="even-space-flex-item"
				>
					<CustomDropdown
						id={`qualification-dropdown`}
						options={[
							{
								value: "preventativeMaintenance",
								title: translation("PreventativeMaintenance"),
								renderValue: translation("PreventativeMaintenanceAbb"),
							},
							{
								value: "installationQualification",
								title: translation("InstallationQualification"),
								renderValue: translation("InstallationQualificationAbb"),
							},
							{
								value: "operationalQualification",
								title: translation("OperationalQualification"),
								renderValue: translation("OperationalQualificationAbb"),
							},
							{
								value: "performanceQualification",
								title: translation("PerformanceQualification"),
								renderValue: translation("PerformanceQualificationAbb"),
							},
						]}
						value={Object.entries(instrumentQualification)
							.filter(([key, value]) => key !== "enabled" && value)
							.map(([key]) => key)}
						setValue={(selectedValues: string[]) => {
							setInstrumentQualification((prev) => ({
								...prev,

								preventativeMaintenance: selectedValues.includes(
									"preventativeMaintenance"
								),
								installationQualification: selectedValues.includes(
									"installationQualification"
								),
								operationalQualification: selectedValues.includes(
									"operationalQualification"
								),
								performanceQualification: selectedValues.includes(
									"performanceQualification"
								),
							}));
						}}
						className="even-space-flex-item"
						smallSize
						multiple
						displayEmpty
						disabled={disabledService}
						hasError={IQHasError}
						placeholder={translation("SelectQualifications")}
					></CustomDropdown>
				</div>
			</div>
			{isServiceOnly && (
				<div className="service-option-form-item">
					<CustomCheckbox
						label={translation("Repairs")}
						labelClassName={"typography-body-01"}
						checked={repair.enabled}
						setChecked={() => {
							setRepair((prev) => ({
								...prev,
								enabled: !repair.enabled,
							}));
						}}
						className="even-space-flex-item"
						disabled={disabledService}
					/>
					<div
						style={{
							visibility: repair.enabled ? "visible" : "hidden",

							// width: "200px", // fixed width to avoid shrinking when hidden
						}}
						className="even-space-flex-item"
					>
						<CustomDropdown
							id={`repairs-dropdown`}
							options={Object.values(RepairOptions).map((value) => {
								return {
									value: value,
									title: getRepairOptionTranslationText(value, translation),
								};
							})}
							value={repair.option}
							setValue={(value) =>
								setRepair((prev) => ({
									...prev,
									option: value,
								}))
							}
							className="even-space-flex-item"
							smallSize
							disabled={disabledService}
						></CustomDropdown>
					</div>
				</div>
			)}
			{isServiceOnly && (
				<div className="service-option-form-item">
					<CustomCheckbox
						label={translation("ServiceContracts")}
						labelClassName={"typography-body-01"}
						checked={hasServiceContracts}
						setChecked={setHasServiceContracts}
						className="even-space-flex-item"
						disabled={disabledService}
					/>
					<div
						style={{
							visibility: hasServiceContracts ? "visible" : "hidden",

							// width: "200px", // fixed width to avoid shrinking when hidden
						}}
						className="even-space-flex-item"
					>
						<CustomNumberInput
							id={`repairs-dropdown`}
							value={serviceContractsTime}
							setValue={setServiceContractsTime}
							className="even-space-flex-item"
							smallSize
							endAdornment={
								<InputAdornment position="end">
									<span>{translation("Months")}</span>
								</InputAdornment>
							}
							disabled={disabledService}
							errorMsg={serviceContractsErrorMsg}
							setErrorMsg={setServiceContractsErrorMsg}
						></CustomNumberInput>
					</div>
				</div>
			)}
		</div>
	);
	return (
		<Card id="item-info-content" className="card-equal-padding">
			{/* <div id="new-sample-header">
				<StepsBar
					items={headerItems}
					currentItem={currentPageIndex - 1}
					setCurrentItem={setCurrentPageIndex}
					icons={[]}
				></StepsBar>
			</div>
			<hr style={{ width: "100%", color: NeutralColor.NEUTRAL_GREY_200 }}></hr> */}
			<div className="item-info-session">
				<div className="item-info-left-session">
					<div className="center-align-with-gap">
						<InstrumentInfoIcon></InstrumentInfoIcon>
						<span className="typography-heading-04 typography-bold">
							{translation("InstrumentInfo")}
						</span>
					</div>
					{!isServiceOnly && instrumentPriceCard}
				</div>
				<div
					style={{
						flex: "6",
						display: "flex",
						flexDirection: "column",
						gap: "16px",
					}}
				>
					<div style={{ display: "flex", gap: "3px" }}>
						{conditionSelector}
						{typeSelector}
						{isServiceOnly && sourceSelector}
					</div>
					<CustomTextInput
						id="item-name-input"
						label={`${translation("InstrumentBrandAndName")}`}
						value={productName}
						setValue={(value) => {
							setProductName(value);
							setUnsavedChanges(true);
						}}
						required
						errorMsg={productNameErrorMsg}
						setErrorMsg={setProductNameErrorMsg}
					/>
					{isServiceOnly && partSerialNumberInput}
					<Divider sx={{ borderStyle: "dashed" }} />
					{warrantyInfo}
					<Divider sx={{ borderStyle: "dashed" }} />
					<CustomTextInput
						id="instrument-description-input"
						label={translation("Description")}
						value={productDescription}
						setValue={(value) => {
							setProductDescription(value);
							setUnsavedChanges(true);
						}}
						isTextArea
					/>
				</div>
			</div>
			<Divider orientation="horizontal" flexItem sx={{ my: "25px" }}></Divider>

			<div className="item-info-session">
				<div className="item-info-left-session">
					<div className="center-align-with-gap">
						<WrenchIcon></WrenchIcon>
						<span className="typography-heading-04 typography-bold">
							{translation("ServiceInfo")}
						</span>
					</div>
					{servicePriceCard}
				</div>
				<div
					style={{
						flex: "6",
						display: "flex",
						flexDirection: "column",
						gap: "16px",
					}}
				>
					<div className="typography-body-01 typography-bold">
						{disabledService
							? translation("AddServiceDescription")
							: translation("ChooseServiceDescription")}
					</div>
					{serviceOptionForm}
					<CustomTextInput
						id="service-description-input"
						label={translation("ServiceDescription")}
						value={serviceDescription}
						setValue={(value) => {
							setServiceDescription(value);
							setUnsavedChanges(true);
						}}
						isTextArea
						disabled={disabledService}
					/>
					<CustomCheckbox
						label={translation("Others")}
						labelClassName={"typography-body-01"}
						checked={hasServiceOtherDescription}
						setChecked={setHasServiceOtherDescription}
						className="even-space-flex-item"
						disabled={disabledService}
					/>
					{hasServiceOtherDescription && (
						<CustomTextInput
							id="other-description-input"
							label={translation("Description")}
							value={serviceOtherDescription}
							setValue={(value) => {
								setServiceOtherDescription(value);
								setUnsavedChanges(true);
							}}
							isTextArea
							disabled={disabledService}
						/>
					)}
					<BlueButton
						id="save-item-to-order-button"
						children={translation("SaveToOrder")}
						size={"flex"}
						// disabled={loading}
						onClick={() => {
							addOrUpdateItem();
						}}
					/>
				</div>
			</div>
		</Card>
	);
}
